import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import styled from 'styled-components';
import CTASection from "../landing/landing_home/empower";
import Footer from "../Footer/footer";
import Navigation from "../navigation/header";
import { useNavigate } from "react-router-dom";
import { FaArrowRightLong as RightArrow } from "react-icons/fa6";
import { colors } from '../constants/Colors';
import { blogPosts } from './utils/blogPosts';
import { blog_bg } from "../../assets/images";



const Blogs = () => {
  const navigate = useNavigate();
  const { type } = useParams();

  const filteredBlogs = type
    ? blogPosts.filter((b) => b.type === type)
    : blogPosts;

  const handleLearnMore = (blog) => {
    navigate(`/blogs/${blog.type}/${blog.blogId}`);
    window.scrollTo(0, 0);
  };

  const getCols = () => {
    const w = window.innerWidth;
    if (w <= 640) return 1;
    if (w <= 991) return 2;
    return 3;
  };

  const [cols, setCols] = useState(getCols());
  const [rowsToShow, setRowsToShow] = useState(2); // 2 rows initially

  useEffect(() => {
    const onResize = () => setCols(getCols());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // reset pagination when category changes
  useEffect(() => {
    setRowsToShow(2);
    window.scrollTo(0, 0);
  }, [type]);

  const visibleCount = useMemo(
    () => Math.min(rowsToShow * cols, filteredBlogs.length),
    [rowsToShow, cols, filteredBlogs.length]
  );

  const canShowMore = visibleCount < filteredBlogs.length;

  return (
    <>
      <FontLink
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap"
        rel="stylesheet"
      />
      <Navigation />

      <HeroContainer>
        <ContentWrapper>
          <TextContent>
            <MainHeading>
              {type
                ? type === "ai-lms"
                  ? "LXP Blogs (Educators)"
                  : type === "ai-lms-students"
                  ? "LXP Blogs (Students)"
                  : type === "smart-forms"
                  ? "Smart Forms Blogs"
                  : type === "ai-assistant"
                  ? "AI Assistant Blogs"
                  : "Our Blogs"
                : "Our Blogs"}
            </MainHeading>
            <Subtitle>
              Explore expert perspectives, AI trends, and real-world impact stories on how
              generative AI is transforming student engagement, enrollment strategies, and
              institutional success.
            </Subtitle>
          </TextContent>
        </ContentWrapper>
      </HeroContainer>

      <Container>
        {filteredBlogs.length === 0 ? (
          <EmptyState>
            <p>No blogs in this category yet.</p>
            <BackLink onClick={() => navigate("/blog")}>See all blogs</BackLink>
          </EmptyState>
        ) : (
          <GridContainer role="list">
            {filteredBlogs.slice(0, visibleCount).map((item) => (
              <CardContainer key={item.blogId} role="listitem">
                <CardImage src={item.imageUrl} alt={item.title} objectPosition={item.objectPosition} />
                <CardContent>
                  <DateLabel>{item.date}</DateLabel>
                  <ContentSection>
                    <Title>{item.title}</Title>
                    <LearnMoreButton
                      aria-label={`Read blog: ${item.title}`}
                      onClick={() => handleLearnMore(item)}
                    >
                      <ButtonContent>
                        <ButtonText>Learn More</ButtonText>
                        <RightArrow color={colors.PRIMARY_PURPLE} />
                      </ButtonContent>
                    </LearnMoreButton>
                  </ContentSection>
                </CardContent>
              </CardContainer>
            ))}
          </GridContainer>
        )}
      </Container>

      {canShowMore && (
        <ShowMoreWrap>
          <ShowMoreBtn onClick={() => setRowsToShow((r) => r + 2)}>
            Show More <RightArrow />
          </ShowMoreBtn>
        </ShowMoreWrap>
      )}

      <CTASection />
      <Footer />
    </>
  );
};


export default Blogs;

const NotFoundWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;  
  `;

const BackLink = styled.button`
  margin-top: 12px;
  padding: 10px 16px;
  border-radius: 100px;
  border: 1px solid #6a37e3;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background-color: rgba(106, 55, 227, 0.05);
  }
`;
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;  
  `;

const GridContainer = styled.main`
  max-width: none;
  margin-left: auto;
  margin-right: auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: center;
  gap: 30px;
  margin: 0 auto;
  padding: 40px 10px;
  box-sizing: border-box;
  background-color:#E6E6E6;


  @media (max-width: 991px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(1, 1fr);
    padding: 15px;
  }
`;


const Container = styled.div`
 
  display: flex;
  width: 100%;
 
  background-color:#E6E6E6;

  @media (max-width: 991px) {
    max-width: 991px;
    width: 100%;
    gap: 40px;
    padding: 20px;
  }

  @media (max-width: 640px) {
    max-width: 640px;
    gap: 30px;
    padding: 15px 0;
  }
`;

const GridRow = styled.section`
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;

  @media (max-width: 991px) {
    flex-direction: column;
    gap: 20px;
    align-items: center;
  }
`;


const CardContainer = styled.article`
  display: flex;
  max-width: 360px;
  height: 360px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  background-color: #fff;

  @media (max-width: 991px) {
    width: 100%;
    max-width: 500px;
  }

  @media (max-width: 640px) {
    max-width: 100%;
  }
`;

const CardImage = styled.img`
  height: 140px;
  width: 100%;
  border-radius: 16px 16px 0 0;
  object-fit: cover;
  object-position: ${({ objectPosition }) => objectPosition || "center"};
`;

const CardContent = styled.div`
  display: flex;
  padding: 0 16px 16px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
`;

const DateLabel = styled.time`
  width: 100%;
  color: rgba(28, 27, 31, 0.7);
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  width: 100%;
`;

const Title = styled.h2`
  width: 100%;
  color: #1c1b1f;
  letter-spacing: -0.18px;
  font: 700 18px/140% Inter, sans-serif;
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const LearnMoreButton = styled.button`
  display: flex;
  padding: 10px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 100px;
  border: 1px solid #6a37e3;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(106, 55, 227, 0.05);
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ButtonText = styled.span`
  color: #6a37e3;
  letter-spacing: -0.075px;
  font: 700 15px/140% Inter, sans-serif;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const FontLink = styled.link``;

const HeroContainer = styled.section`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 420px;
  // width: 97%;
  overflow: hidden;
  position: relative;
  padding: 90px 20px 40px;

  background: linear-gradient(
      to bottom,
      rgba(96, 145, 251, 0) 0%,       /* Transparent at top */
      rgba(96, 145, 251, 0.31) 60%,    /* Slight gradient mid-way */
      rgba(99, 34, 249, 0.49) 100%     /* Deep purple at bottom */
    ), url(${blog_bg}) top center / cover no-repeat;

  background-blend-mode: normal;

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 640px) {
    padding: 20px 16px;
    height: 520px;
  }
`;


const ContentWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 939px;
  flex-direction: column;
  align-items: center;
  gap: 32px;

  @media (max-width: 991px) {
    max-width: 700px;
    gap: 28px;
  }

  @media (max-width: 640px) {
    gap: 24px;
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const MainHeading = styled.h1`
  width: 100%;
  color: #fff;
  text-align: center;
  text-shadow: 0 4px 4px rgba(0, 0, 0, 0.2);
  letter-spacing: -0.6px;
  font: 700 60px/70px 'Inter', sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 48px;
    line-height: 56px;
  }

  @media (max-width: 640px) {
    font-size: 32px;
    line-height: 40px;
    letter-spacing: -0.4px;
  }
`;

const Subtitle = styled.p`
  color: #fff;
  text-align: center;
  letter-spacing: -0.08px;
  font: 500 16px/160% 'Inter', sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const ShowMoreWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 0 40px;
  background-color: #E6E6E6;
`;
const ShowMoreBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 22px;
  border-radius: 999px;
  border: 1px solid #FFFFFFe7;
  background: linear-gradient(45deg, #5EA1FF, #6A37E3);
  font: 700 15px/1 Inter, sans-serif;
  color: #FFFFFF;
  cursor: pointer;
  transition: transform .15s ease, background .2s ease;

  // &:hover { background: rgba(72, 27, 177, 0.06); transform: translateY(-1px); }
  // &:active { transform: translateY(0); }
`;